import React from 'react';
import PropTypes from 'prop-types';
import Auth from '@Services/Auth';
import { FEATURES } from '@Constants/rx-ui/constants';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';
import DocumentCustomLink from '@Components/Results/documents/DocumentCustomLink';
import FullText from '@Components/Results/reactions/reaction-details/FullText';
import { displayConditions } from '@Components/Results/documents/DocumentExpandSections';
import { ButtonWithIcon } from '@els/els-react--button';
import { Pill } from '@els/els-react--pill';

function DocumentButtons({ document: citation, toggleSection, getTrackingAttrs, metadata, ...props }) {
  const generateButton = (flag, label, count, customAction) => {
    const trackingAttrs = getTrackingAttrs({
      'data-tracking': !props[flag],
      'data-tracking-type': 'contentview,contentlinkclick',
      'data-tracking-action': label,
    });
    const onClick = customAction ? (e) => customAction(e, flag, toggleSection) : (e) => toggleSection(e, flag);
    return (
      <ButtonWithIcon
        type="link"
        sprite={props[flag] ? 'ChevronUp' : 'ChevronDown'}
        iconRight
        linkIconSize="xs"
        className={`e2e-action e2e-document-action-${label.toLowerCase()}`}
        onClick={onClick}
        {...trackingAttrs}>
        <span className="u-els-color-link-inline">{label}</span>
        {count && (
          <Pill pillColor="extended-blue-0" textColor="secondary" condensed className="u-els-margin-left-1o4">
            {count}
          </Pill>
        )}
      </ButtonWithIcon>
    );
  };

  return (
    <>
      <div className="result-detail-row-btns">
        <div className="els-btn-group-left">
          {/* Abstract */}
          {displayConditions.abstractExpanded(citation) && generateButton('abstractExpanded', 'Abstract')}
          {/* Index Terms */}
          {displayConditions.indextermsExpanded(citation) && generateButton('indextermsExpanded', 'Index Terms')}
          {/* Claims */}
          {displayConditions.claimsExpanded(citation) && generateButton('claimsExpanded', 'Claims')}
          {/* Bibliographic Info */}
          {displayConditions.bibliographicInfoExpanded(citation) &&
            generateButton(
              'bibliographicInfoExpanded',
              !Auth.isFeatureEnabled(FEATURES.disablePatentSightUIChanges) ? 'Bibliographic Info' : 'Front Page Info'
            )}
          {/* Substances */}
          {displayConditions.substancesExpanded(citation) && generateButton('substancesExpanded', 'Substances', citation.substancesCount)}
          {/* Reactions */}
          {displayConditions.reactionsExpanded(citation) && generateButton('reactionsExpanded', 'Reactions', citation.reactionsCount)}
          {/* Targets */}
          {displayConditions.targetsExpanded(citation) && generateButton('targetsExpanded', 'Targets')}
          {/* Full Text */}
          {citation.fullTextCustom && <DocumentCustomLink citation={citation} getTrackingAttrs={getTrackingAttrs} />}
          {!citation.fullTextCustom && (
            <FullText
              citation={citation}
              trackingAttributes={{
                index: citation.index,
                getTrackingAttrs: getTrackingAttrs(),
              }}
              metadata={metadata}
            />
          )}
        </div>
      </div>
      {(displayConditions.hitSubstancesExpanded(citation) || displayConditions.hitReactionsExpanded(citation)) && (
        <div className="result-detail-row-btns">
          <div className="els-btn-group-left">
            {/* Hit Substances */}
            {displayConditions.hitSubstancesExpanded(citation) &&
              generateButton('hitSubstancesExpanded', 'Hit Substances', citation.substancesHitCount)}
            {/* Hit Reactions */}
            {displayConditions.hitReactionsExpanded(citation) &&
              generateButton('hitReactionsExpanded', 'Hit Reactions', citation.reactionsHitCount)}
          </div>
        </div>
      )}
    </>
  );
}

DocumentButtons.propTypes = {
  document: PropTypes.object.isRequired,
  toggleSection: PropTypes.func.isRequired,
  abstractExpanded: PropTypes.bool.isRequired,
  claimsExpanded: PropTypes.bool.isRequired,
  indextermsExpanded: PropTypes.bool.isRequired,
  bibliographicInfoExpanded: PropTypes.bool.isRequired,
  substancesExpanded: PropTypes.bool.isRequired,
  reactionsExpanded: PropTypes.bool.isRequired,
  targetsExpanded: PropTypes.bool.isRequired,
  hitSubstancesExpanded: PropTypes.bool.isRequired,
  hitReactionsExpanded: PropTypes.bool.isRequired,
  getTrackingAttrs: PropTypes.func.isRequired,
  metadata: MetadataPropTypesShape,
};

export default DocumentButtons;
